import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X, Instagram, Twitter, Facebook, Linkedin, Youtube, Globe } from "lucide-react";

import ReservationForm from "../components/ReservationForm";
import {
    SITE,
    STATS,
    MENU_INTRO,
    MENU_SECTIONS,
    RESERVATIONS,
    ABOUT,
    CONTACT,
    HEADER,
    FOOTER,
    withElStyle,
} from "../lib/_data";

const DEFAULT_NAV = [
    { id: "menu", label: "Menu" },
    { id: "reservations", label: "Reservations" },
    { id: "about", label: "About" },
    { id: "contact", label: "Contact" },
];

const SOCIAL_ICON = {
    instagram: Instagram,
    twitter: Twitter,
    x: Twitter,
    facebook: Facebook,
    linkedin: Linkedin,
    youtube: Youtube,
    tiktok: Globe,
    pinterest: Globe,
    threads: Globe,
};

export default function RestaurantLanding({ slug }) {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollTo = (idOrHref) => {
        setMobileOpen(false);
        if (!idOrHref) return;
        // If it looks like an external/internal link (starts with http, /, or
        // mailto/tel), navigate. Otherwise treat it as an anchor id.
        if (/^(https?:|mailto:|tel:|\/)/i.test(idOrHref)) {
            window.location.href = idOrHref;
            return;
        }
        const cleanId = idOrHref.replace(/^#/, "");
        const el = document.getElementById(cleanId);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    // Merge NAV: prefer edited HEADER.nav_items, fall back to defaults.
    const nav = (Array.isArray(HEADER.nav_items) && HEADER.nav_items.length > 0)
        ? HEADER.nav_items.map((n, i) => ({
            id: (n.link || "").replace(/^#/, "").split("/").pop() || `nav-${i}`,
            label: n.label || "",
            href: n.link || `#${(n.label || "").toLowerCase().replace(/\s+/g, "-")}`,
        }))
        : DEFAULT_NAV.map((n) => ({ ...n, href: `#${n.id}` }));

    const brandText = HEADER.logo_text || SITE.brand;
    const headerCtaText = HEADER.cta_text || "";
    const headerCtaLink = HEADER.cta_link || "";
    const headerCtaNewTab = !!HEADER.cta_open_new_tab;

    // Footer data with fallbacks.
    const footerTagline = FOOTER.tagline || "";
    const footerCopyright = FOOTER.copyright || CONTACT.copyright || `© ${SITE.brand}`;
    const footerSocials = (Array.isArray(FOOTER.socials) ? FOOTER.socials : [])
        .filter((s) => s && s.platform && SOCIAL_ICON[s.platform]);
    const footerQuickLinks = Array.isArray(FOOTER.quick_links) ? FOOTER.quick_links : [];
    const footerNewsletterOn = !!FOOTER.newsletter_enabled;

    return (
        <div className="min-h-screen bg-cream-50 text-forest-800">
            {/* Nav */}
            <header
                data-testid="site-header"
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                    scrolled
                        ? "bg-cream-50/85 backdrop-blur-xl border-b border-cream-200 text-forest-800"
                        : "bg-transparent text-cream-50"
                }`}
            >
                <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-16 h-20 flex items-center justify-between">
                    <button
                        data-testid="site-nav"
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        className="font-serif text-2xl tracking-tight flex items-center gap-2"
                    >
                        {HEADER.logo_image_url ? (
                            <img
                                src={HEADER.logo_image_url}
                                alt={brandText || "Logo"}
                                className="h-8 w-8 rounded-full object-cover"
                                data-testid="nav-brand-image"
                            />
                        ) : null}
                        <span data-testid="nav-brand">{brandText}</span>
                    </button>
                    <nav className="hidden md:flex items-center gap-10">
                        {nav.map((n, i) => (
                            <button
                                key={`${n.id}-${i}`}
                                data-testid={`nav-${n.id}`}
                                onClick={() => scrollTo(n.href)}
                                className="label link-underline transition-colors"
                            >
                                {n.label}
                            </button>
                        ))}
                        {headerCtaText && (
                            <a
                                href={headerCtaLink || "#"}
                                target={headerCtaNewTab ? "_blank" : undefined}
                                rel={headerCtaNewTab ? "noreferrer" : undefined}
                                data-testid="nav-cta"
                                className="px-5 py-2 rounded-full bg-forest-800 text-cream-50 label"
                            >
                                {headerCtaText}
                            </a>
                        )}
                    </nav>
                    <button
                        data-testid="nav-mobile-toggle"
                        className="md:hidden p-2"
                        onClick={() => setMobileOpen((v) => !v)}
                        aria-label="Menu"
                    >
                        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
                {mobileOpen && (
                    <div className="md:hidden border-t border-cream-200 bg-cream-50">
                        <div className="px-6 py-6 flex flex-col gap-4">
                            {nav.map((n, i) => (
                                <button
                                    key={`${n.id}-m-${i}`}
                                    data-testid={`nav-mobile-${n.id}`}
                                    onClick={() => scrollTo(n.href)}
                                    className="label text-left"
                                >
                                    {n.label}
                                </button>
                            ))}
                            {headerCtaText && (
                                <a
                                    href={headerCtaLink || "#"}
                                    target={headerCtaNewTab ? "_blank" : undefined}
                                    rel={headerCtaNewTab ? "noreferrer" : undefined}
                                    data-testid="nav-mobile-cta"
                                    className="label text-left text-forest-800 font-bold"
                                >
                                    {headerCtaText}
                                </a>
                            )}
                        </div>
                    </div>
                )}
            </header>

            {/* Hero — full-width banner */}
            <section id="hero" className="relative w-full h-screen min-h-[640px] overflow-hidden">
                {SITE.hero_image ? (
                    <img
                        data-testid="hero-image"
                        src={SITE.hero_image}
                        alt={SITE.brand}
                        className="absolute inset-0 w-full h-full object-cover"
                        data-testid="hero-image"
                    />
                ) : (
                    <div
                        className="absolute inset-0 w-full h-full"
                        style={{ background: "linear-gradient(135deg, #1A1C19 0%, #2C3522 60%, #3F4A32 100%)" }}
                        data-testid="hero-image-fallback"
                    />
                )}
                {/* Editorial dark gradient for text legibility */}
                <div className="absolute inset-0 bg-gradient-to-b from-forest-900/60 via-forest-900/25 to-forest-900/75 pointer-events-none" />

                {/* Top-right corner mark */}
                {(SITE.hero_top_right_1 || SITE.hero_top_right_2) && (
                    <div className="absolute top-24 right-6 sm:right-12 lg:right-16 hidden sm:block fade-up">
                        {SITE.hero_top_right_1 && (
                            <p className="label text-cream-200 text-right" data-testid="hero-top-right-1">{SITE.hero_top_right_1}</p>
                        )}
                        {SITE.hero_top_right_2 && (
                            <p className="font-serif italic text-cream-50 text-right text-lg mt-1" data-testid="hero-top-right-2">
                                {SITE.hero_top_right_2}
                            </p>
                        )}
                    </div>
                )}

                {/* Bottom-anchored copy */}
                <div className="absolute inset-x-0 bottom-0">
                    <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-16 pb-16 sm:pb-24 lg:pb-28">
                        <div className="max-w-4xl fade-up">
                            {SITE.eyebrow && (
                                <p className="label text-cream-200 mb-8" data-testid="hero-eyebrow">
                                    {SITE.eyebrow}
                                </p>
                            )}
                            <h1
                                data-testid="hero-title"
                                className="font-serif text-6xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tighter text-cream-50"
                                style={{ textShadow: "0 2px 40px rgba(0,0,0,0.35)" }}
                            >
                                {SITE.hero_title}
                                {SITE.hero_title_em && (
                                    <>
                                        <br />
                                        <em className="italic text-cream-100">{SITE.hero_title_em}</em>
                                    </>
                                )}
                            </h1>
                            {SITE.hero_subtitle && (
                                <p
                                    data-testid="hero-subtitle"
                                    className="mt-8 max-w-xl text-lg leading-relaxed text-cream-100 font-light"
                                >
                                    {SITE.hero_subtitle}
                                </p>
                            )}
                            <div className="mt-10 flex flex-wrap items-center gap-6">
                                <button
                                    data-testid="hero-reserve-btn"
                                    onClick={() => scrollTo("reservations")}
                                    className="bg-cream-50 text-forest-800 hover:bg-cream-100 transition-all duration-500 px-8 py-4 label"
                                >
                                    {SITE.hero_reserve_cta || "Reserve a Table"}
                                </button>
                                <button
                                    data-testid="hero-menu-btn"
                                    onClick={() => scrollTo("menu")}
                                    className="label link-underline text-cream-50"
                                >
                                    {SITE.hero_menu_cta || "View the Menu"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scroll cue */}
                <div className="absolute bottom-6 right-6 sm:right-12 lg:right-16 hidden md:block">
                    <div className="flex items-center gap-3">
                        <span className="label text-cream-200" data-testid="hero-scroll-cue">{SITE.scroll_label || "Scroll"}</span>
                        <div className="w-8 h-px bg-cream-200" />
                    </div>
                </div>
            </section>

            {/* Brand info strip */}
            {STATS.length > 0 && (
                <div className="border-b border-cream-200 py-6 bg-cream-50" data-testid="restaurant-hero-stats">
                    <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-16 flex flex-wrap justify-between gap-x-12 gap-y-4">
                        {STATS.map((s, i) => (
                            <React.Fragment key={i}>
                                <span className="label text-forest-500" data-testid={`stat-${i + 1}`}>
                                    {s}
                                </span>
                                {i < STATS.length - 1 && <span className="label text-forest-500">·</span>}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            )}

            {/* Menu */}
            <section id="menu" data-testid="menu-preview" className="py-24 sm:py-32 lg:py-40 relative">
                <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-16">
                    <div className="grid lg:grid-cols-12 gap-10 mb-20">
                        <div className="lg:col-span-4">
                            <p className="label text-forest-500 mb-6" data-testid="restaurant-menu-eyebrow">{MENU_INTRO.eyebrow}</p>
                            <h2 className="font-serif text-5xl sm:text-6xl leading-tight tracking-tight text-forest-800">
                                <span data-testid="restaurant-menu-title">{MENU_INTRO.title}</span>
                                {MENU_INTRO.title_em && (
                                    <>
                                        <br />
                                        <em className="italic" data-testid="restaurant-menu-title-em">{MENU_INTRO.title_em}</em>
                                    </>
                                )}
                            </h2>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7">
                            <p className="text-forest-500 leading-relaxed text-lg" data-testid="restaurant-menu-blurb">{MENU_INTRO.blurb}</p>
                            {MENU_INTRO.price_line && (
                                <p className="mt-6 font-mono text-sm text-forest-500" data-testid="menu-price">
                                    {MENU_INTRO.price_line}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-x-16 gap-y-20">
                        {MENU_SECTIONS.map((section, si) => (
                            <motion.div
                                key={`${section.title}-${si}`}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.7, delay: si * 0.05 }}
                                data-testid={`menu-section-${si}`}
                            >
                                <div className="flex items-baseline justify-between mb-8 pb-4 border-b border-cream-200">
                                    <h3 className="font-serif text-3xl italic text-forest-800">{section.title}</h3>
                                    {section.subtitle && (
                                        <span className="label text-forest-500">{section.subtitle}</span>
                                    )}
                                </div>
                                <ul className="space-y-8">
                                    {(section.items || []).map((it, i) => (
                                        <li
                                            key={`${it.name}-${i}`}
                                            data-testid={`menu-item-${si}-${i}`}
                                            className="flex items-start gap-6"
                                        >
                                            <div className="flex-1">
                                                <p className="font-serif text-xl text-forest-800">{it.name}</p>
                                                {it.desc && (
                                                    <p className="text-sm text-forest-500 mt-1 leading-relaxed">
                                                        {it.desc}
                                                    </p>
                                                )}
                                            </div>
                                            {it.price && (
                                                <span className="font-mono text-sm text-forest-500 pt-1.5">
                                                    {it.price}
                                                </span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Reservations */}
            <section id="reservations" className="py-24 sm:py-32 lg:py-40 bg-cream-100 relative">
                <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-16 grid lg:grid-cols-12 gap-16">
                    <div className="lg:col-span-5">
                        <p className="label text-forest-500 mb-6" data-testid="restaurant-reservations-eyebrow">{RESERVATIONS.eyebrow}</p>
                        <h2 className="font-serif text-5xl sm:text-6xl leading-tight tracking-tight text-forest-800">
                            <span data-testid="restaurant-reservations-title">{RESERVATIONS.title}</span>
                            {RESERVATIONS.title_em && (
                                <>
                                    <br />
                                    <em className="italic" data-testid="restaurant-reservations-title-em">{RESERVATIONS.title_em}</em>
                                </>
                            )}
                        </h2>
                        <div className="mt-10 space-y-6 text-forest-500" data-testid="reservation-info">
                            {RESERVATIONS.seatings && RESERVATIONS.seatings.length > 0 && (
                                <div>
                                    <p className="label text-forest-800 mb-2">{RESERVATIONS.seatings_label || "Seatings"}</p>
                                    {RESERVATIONS.seatings.map((s, i) => (
                                        <p key={i} className="font-serif text-lg">
                                            {s.time || s.label}
                                            {s.label && s.time && ` · ${s.label}`}
                                        </p>
                                    ))}
                                </div>
                            )}
                            {RESERVATIONS.nights && (
                                <div>
                                    <p className="label text-forest-800 mb-2">{RESERVATIONS.nights_label || "Nights"}</p>
                                    <p className="font-serif text-lg">{RESERVATIONS.nights}</p>
                                </div>
                            )}
                            {RESERVATIONS.duration && (
                                <div>
                                    <p className="label text-forest-800 mb-2">{RESERVATIONS.duration_label || "Duration"}</p>
                                    <p className="font-serif text-lg">{RESERVATIONS.duration}</p>
                                </div>
                            )}
                            {RESERVATIONS.note && (
                                <p className="text-sm leading-relaxed pt-4 border-t border-cream-200 max-w-md">
                                    {RESERVATIONS.note}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <ReservationForm slug={slug} />
                    </div>
                </div>
            </section>

            {/* About */}
            <section id="about" className="py-24 sm:py-32 lg:py-40 relative">
                <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-16 grid lg:grid-cols-12 gap-16 items-center">
                    <div className="lg:col-span-6 order-2 lg:order-1">
                        <p className="label text-forest-500 mb-6" data-testid="restaurant-about-eyebrow">{ABOUT.eyebrow}</p>
                        <h2 className="font-serif text-5xl sm:text-6xl leading-tight tracking-tight text-forest-800">
                            <span data-testid="restaurant-about-title">{ABOUT.title}</span>
                            {ABOUT.title_em && (
                                <>
                                    <br />
                                    <em className="italic" data-testid="restaurant-about-title-em">{ABOUT.title_em}</em>
                                </>
                            )}
                        </h2>
                        <div className="mt-10 space-y-6 text-forest-500 leading-relaxed max-w-xl" data-testid="restaurant-about-text">
                            {(ABOUT.paragraphs || []).map((p, i) => (
                                <p key={i}>{p}</p>
                            ))}
                        </div>
                    </div>
                    <div className="lg:col-span-6 order-1 lg:order-2 relative">
                        <div className="grid grid-cols-5 gap-4">
                            <div className="col-span-3 aspect-[4/5] overflow-hidden bg-cream-100">
                                {ABOUT.image_1 && (
                                    <img
                                        data-testid="about-image-1"
                                        src={ABOUT.image_1}
                                        alt={`${SITE.brand} interior`}
                                        className="w-full h-full object-cover"
                                    />
                                )}
                            </div>
                            <div className="col-span-2 aspect-[3/4] overflow-hidden mt-16 bg-cream-100">
                                {ABOUT.image_2 && (
                                    <img
                                        data-testid="about-image-2"
                                        src={ABOUT.image_2}
                                        alt={`${SITE.brand} dish`}
                                        className="w-full h-full object-cover"
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact */}
            <section id="contact" className="py-24 sm:py-32 lg:py-40 bg-forest-800 text-cream-50">
                <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-16 grid lg:grid-cols-12 gap-16">
                    <div className="lg:col-span-5">
                        <p className="label text-cream-200 mb-6" data-testid="restaurant-contact-eyebrow">{CONTACT.eyebrow}</p>
                        <h2 className="font-serif text-5xl sm:text-6xl leading-tight tracking-tight text-cream-50">
                            <span data-testid="restaurant-contact-title">{CONTACT.title}</span>
                            {CONTACT.title_em && (
                                <>
                                    <br />
                                    <em className="italic" data-testid="restaurant-contact-title-em">{CONTACT.title_em}</em>
                                </>
                            )}
                        </h2>
                    </div>
                    <div className="lg:col-span-7 grid sm:grid-cols-2 gap-12" data-testid="restaurant-contact-info">
                        {CONTACT.address_lines && CONTACT.address_lines.length > 0 && (
                            <div>
                                <p className="label text-cream-200 mb-3">{CONTACT.address_label || "Address"}</p>
                                <p className="font-serif text-2xl leading-snug">
                                    {CONTACT.address_lines.map((line, i) => (
                                        <React.Fragment key={i}>
                                            {line}
                                            {i < CONTACT.address_lines.length - 1 && <br />}
                                        </React.Fragment>
                                    ))}
                                </p>
                            </div>
                        )}
                        {CONTACT.hours_lines && CONTACT.hours_lines.length > 0 && (
                            <div>
                                <p className="label text-cream-200 mb-3">{CONTACT.hours_label || "Hours"}</p>
                                <p className="font-serif text-2xl leading-snug">
                                    {CONTACT.hours_lines.map((line, i) => (
                                        <React.Fragment key={i}>
                                            {line}
                                            {i < CONTACT.hours_lines.length - 1 && <br />}
                                        </React.Fragment>
                                    ))}
                                </p>
                            </div>
                        )}
                        {(CONTACT.phone || CONTACT.email) && (
                            <div>
                                <p className="label text-cream-200 mb-3">{CONTACT.reach_label || "Reach us"}</p>
                                {CONTACT.phone && (
                                    <p className="font-serif text-2xl leading-snug" data-testid="contact-phone">
                                        {CONTACT.phone}
                                    </p>
                                )}
                                {CONTACT.email && (
                                    <p className="font-serif text-lg text-cream-200 mt-2" data-testid="contact-email">
                                        {CONTACT.email}
                                    </p>
                                )}
                            </div>
                        )}
                        {CONTACT.private_events_email && (
                            <div>
                                <p className="label text-cream-200 mb-3">{CONTACT.private_events_label || "Private events"}</p>
                                <p className="font-serif text-lg leading-snug text-cream-200">
                                    {CONTACT.private_events_blurb || "For buyouts and private dinners, write to us at"}{" "}
                                    <span className="text-cream-50">{CONTACT.private_events_email}</span>.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
                <div className="border-t border-cream-200/10 mt-24" data-testid="site-footer">
                    <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-16 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                        <div className="max-w-md">
                            {footerTagline && (
                                <p className="label text-cream-200 leading-relaxed" data-testid="footer-tagline-text">
                                    {footerTagline}
                                </p>
                            )}
                        </div>

                        {footerQuickLinks.length > 0 && (
                            <div className="flex flex-wrap gap-x-6 gap-y-2" data-testid="footer-quick-links">
                                {footerQuickLinks.map((l, i) => (
                                    <a
                                        key={`${l.label}-${i}`}
                                        href={l.link || "#"}
                                        data-testid={`footer-quick-link-${i}`}
                                        className="label text-cream-200 hover:text-cream-50"
                                    >
                                        {l.label}
                                    </a>
                                ))}
                            </div>
                        )}

                        {footerSocials.length > 0 && (
                            <div className="flex items-center gap-3" data-testid="footer-socials">
                                {footerSocials.map((s, i) => {
                                    const Icon = SOCIAL_ICON[s.platform];
                                    return (
                                        <a
                                            key={`${s.platform}-${i}`}
                                            href={s.url || "#"}
                                            aria-label={s.platform}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="w-9 h-9 rounded-full border border-cream-200/40 flex items-center justify-center hover:bg-cream-200/10"
                                            data-testid={`footer-social-${s.platform}`}
                                        >
                                            <Icon className="w-4 h-4 text-cream-200" />
                                        </a>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {footerNewsletterOn && (
                        <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-16 pb-8" data-testid="footer-newsletter">
                            <div className="max-w-md">
                                <p className="label text-cream-50 mb-2">
                                    {FOOTER.newsletter_heading || "Stay in the loop"}
                                </p>
                                <p className="label text-cream-200 mb-3 leading-relaxed">
                                    {FOOTER.newsletter_subtitle || "Occasional news about our menu and events."}
                                </p>
                                <form
                                    onSubmit={(e) => e.preventDefault()}
                                    className="flex gap-2"
                                >
                                    <input
                                        type="email"
                                        placeholder="you@example.com"
                                        className="flex-1 rounded-md bg-forest-800 text-cream-50 px-3 py-2 label border border-cream-200/20 focus:border-cream-50 focus:outline-none"
                                        data-testid="footer-newsletter-input"
                                    />
                                    <button
                                        type="submit"
                                        className="px-4 py-2 rounded-md bg-cream-50 text-forest-800 label font-bold"
                                        data-testid="footer-newsletter-submit"
                                    >
                                        {FOOTER.newsletter_cta_text || "Subscribe"}
                                    </button>
                                </form>
                            </div>
                        </div>
                    )}

                    <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-16 py-6 border-t border-cream-200/10 flex flex-wrap justify-between items-center gap-4">
                        <p className="label text-cream-200" data-testid="footer-copyright">
                            {footerCopyright}
                        </p>
                        <p className="label text-cream-200" data-testid="footer-appointment-note">{FOOTER.appointment_note || "Reservations by appointment"}</p>
                    </div>
                </div>
            </section>
        </div>
    );
}
